
import React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';

const jobFormSchema = z.object({
  title: z.string().min(3, { message: 'Job title must be at least 3 characters' }),
  company: z.string().min(2, { message: 'Company name must be at least 2 characters' }),
  location: z.string().min(2, { message: 'Location must be at least 2 characters' }),
  description: z.string().min(20, { message: 'Description must be at least 20 characters' }),
  requirements: z.string().min(10, { message: 'Requirements must be at least 10 characters' }),
  salary_range: z.string().optional(),
  application_url: z.string().url({ message: 'Must be a valid URL' }).or(z.string().length(0))
});

type JobFormValues = z.infer<typeof jobFormSchema>;

const JobPostForm = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  
  const form = useForm<JobFormValues>({
    resolver: zodResolver(jobFormSchema),
    defaultValues: {
      title: '',
      company: '',
      location: '',
      description: '',
      requirements: '',
      salary_range: '',
      application_url: '',
    }
  });
  
  const onSubmit = async (data: JobFormValues) => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "You must be logged in to post a job",
        variant: "destructive"
      });
      return;
    }
    
    try {
      // In a real app, you would insert this into a jobs table
      toast({
        title: "Job Posted",
        description: `Your job "${data.title}" has been posted successfully.`
      });
      
      form.reset();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to post job",
        variant: "destructive"
      });
    }
  };
  
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-white">Job Title</FormLabel>
              <FormControl>
                <Input 
                  placeholder="e.g. Senior React Developer" 
                  className="bg-forest border-mint/30 text-white placeholder:text-white/50"
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="company"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white">Company Name</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="e.g. Acme Inc" 
                    className="bg-forest border-mint/30 text-white placeholder:text-white/50"
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="location"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white">Location</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="e.g. Remote, San Francisco, CA" 
                    className="bg-forest border-mint/30 text-white placeholder:text-white/50"
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-white">Job Description</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="Describe the role, responsibilities, etc."
                  className="bg-forest border-mint/30 text-white placeholder:text-white/50 min-h-[120px]"
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="requirements"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-white">Requirements</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="List the required skills, experience, etc."
                  className="bg-forest border-mint/30 text-white placeholder:text-white/50"
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="salary_range"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white">Salary Range (Optional)</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="e.g. $80,000 - $100,000" 
                    className="bg-forest border-mint/30 text-white placeholder:text-white/50"
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="application_url"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white">Application URL (Optional)</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="e.g. https://yourcompany.com/careers" 
                    className="bg-forest border-mint/30 text-white placeholder:text-white/50"
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        <div className="flex justify-end pt-4">
          <Button type="submit" className="bg-mint text-forest hover:bg-mint/90">
            Post Job
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default JobPostForm;
